import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Ticket, DollarSign } from 'lucide-react';
import { toast } from 'sonner';

const RafflesPage = () => {
  const navigate = useNavigate();
  const { adminSlug } = useParams();
  const [raffles, setRaffles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adminInfo, setAdminInfo] = useState(null);

  useEffect(() => {
    if (adminSlug) {
      fetchAdminInfo();
    }
    fetchRaffles();
  }, [adminSlug]);

  const fetchAdminInfo = async () => {
    try {
      const response = await axios.get(`${API}/admins/public/${adminSlug}`);
      setAdminInfo(response.data);
    } catch (error) {
      console.error('Error fetching admin info:', error);
      // No mostramos error, usamos defaults
    }
  };

  const fetchRaffles = async () => {
    try {
      let response;
      if (adminSlug) {
        // Obtener rifas de un admin específico
        response = await axios.get(`${API}/raffles/public/${adminSlug}`);
      } else {
        // Obtener todas las rifas activas (legacy)
        response = await axios.get(`${API}/raffles?active_only=true`);
      }
      setRaffles(response.data);
    } catch (error) {
      console.error('Error fetching raffles:', error);
      if (error.response?.status === 404) {
        toast.error('Administrador no encontrado');
      } else {
        toast.error('Error al cargar los sorteos');
      }
    } finally {
      setLoading(false);
    }
  };

  const getRaffleTypeLabel = (type) => {
    switch (type) {
      case 'grid_100':
        return '00-99';
      case 'classic':
        return '000-999';
      case 'grid_10000':
        return '0000-9999';
      case 'lines':
        return '200 Líneas x 5';
      case 'lines_125':
        return '125 Líneas x 8';
      case 'lines_250':
        return '250 filas Manu';
      case 'lines_250_osvaldo':
        return '250 Líneas Osvaldo';
      case 'lines_50':
        return '50 Líneas x 2';
      case 'lines_500':
        return '500 Líneas x 2';
      case 'lines_20':
        return '20 Líneas x 5';
      case 'custom':
        return 'Personalizada';
      default:
        return type;
    }
  };

  const getAvailableCount = (raffle) => raffle.stats?.available || 0;

  // Función para generar el estilo de fondo personalizado
  const getBackgroundStyle = () => {
    const primaryColor = adminInfo?.primary_color || '#9333ea';
    const backgroundImage = adminInfo?.background_image;

    if (backgroundImage) {
      return {
        backgroundImage: `linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url(${backgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed'
      };
    }

    // Generar gradiente con el color personalizado
    const secondaryColor = adjustColor(primaryColor, -30);
    const tertiaryColor = adjustColor(primaryColor, 40);
    return {
      background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 50%, ${tertiaryColor} 100%)`
    };
  };

  // Función auxiliar para ajustar brillo del color
  function adjustColor(hex, percent) {
    const num = parseInt(hex.replace('#', ''), 16);
    const amt = Math.round(2.55 * percent);
    const R = Math.max(Math.min((num >> 16) + amt, 255), 0);
    const G = Math.max(Math.min((num >> 8 & 0x00FF) + amt, 255), 0);
    const B = Math.max(Math.min((num & 0x0000FF) + amt, 255), 0);
    return '#' + (0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1);
  }

  // Obtener color para botones y acentos
  const getPrimaryColor = () => adminInfo?.primary_color || '#9333ea';

  if (loading) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center"
        style={getBackgroundStyle()}
      >
        <div className="text-white text-2xl">Cargando sorteos...</div>
      </div>
    );
  }

  // Si no hay adminSlug, mostrar mensaje de acceso por link
  if (!adminSlug) {
    return (
      <div 
        className="min-h-screen p-4 md:p-8"
        style={getBackgroundStyle()}
      >
        <div className="max-w-3xl mx-auto">
          <Button
            variant="outline"
            onClick={() => navigate('/')}
            className="mb-6 bg-white/10 text-white hover:bg-white/20 border-white/30"
            data-testid="back-to-home-button"
          >
            <ArrowLeft className="mr-2" size={20} />
            Volver al Inicio
          </Button>

          <Card className="bg-white/95 backdrop-blur-lg text-center">
            <CardContent className="p-12">
              <Ticket size={80} className="mx-auto mb-6 text-purple-600" />
              <h2 className="text-3xl font-bold mb-4">Acceso por Link Personalizado</h2>
              <p className="text-lg text-gray-600 mb-6">
                Para ver los sorteos, necesitás el link personalizado del organizador.
              </p>
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-6 text-left">
                <p className="text-sm text-gray-700 mb-2">
                  <strong>¿Sos organizador?</strong>
                </p>
                <p className="text-sm text-gray-600 mb-4">
                  Encontrá tu link personalizado en el panel de administración y compartilo con tus clientes.
                </p>
                <p className="text-sm text-gray-700 mb-2">
                  <strong>¿Querés participar?</strong>
                </p>
                <p className="text-sm text-gray-600">
                  Solicitá el link al organizador del sorteo.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen p-3 md:p-8 pb-20"
      style={getBackgroundStyle()}
    >
      <div className="max-w-7xl mx-auto">
        <Button
          variant="outline"
          onClick={() => navigate('/')}
          className="mb-3 md:mb-6 bg-white/10 text-white hover:bg-white/20 border-white/30 text-sm md:text-base py-2 px-3 md:py-2 md:px-4"
          data-testid="back-to-home-button"
        >
          <ArrowLeft className="mr-1 md:mr-2" size={16} />
          Volver al Inicio
        </Button>

        <div className="text-center mb-4 md:mb-12">
          {adminInfo?.name ? (
            <h1 className="text-white mb-2 md:mb-4" data-testid="rifas-title">
              <span className="block text-sm md:text-lg font-normal opacity-80">Sorteos de</span>
              <span className="block text-3xl md:text-5xl font-bold">{adminInfo.name}</span>
            </h1>
          ) : (
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-2 md:mb-4" data-testid="rifas-title">
              Sorteos Activos
            </h1>
          )}
          <p className="text-sm md:text-xl text-white/80">
            Elegí tu sorteo y reservá tus números
          </p>
        </div>

        {raffles.length === 0 ? (
          <Card className="bg-white/10 backdrop-blur-lg border-white/20">
            <CardContent className="p-8 md:p-12 text-center">
              <Ticket size={48} className="mx-auto mb-3 text-white/50" />
              <p className="text-white text-lg md:text-xl">No hay sorteos activos en este momento</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {raffles.map((raffle) => (
              <Card
                key={raffle.id}
                className="bg-white/95 backdrop-blur-lg hover:shadow-2xl transition-all cursor-pointer"
                onClick={() => navigate(`/rifa/${raffle.id}`)}
                data-testid={`raffle-card-${raffle.id}`}
              >
                <CardHeader className="p-4 md:p-6 pb-2 md:pb-3">
                  {raffle.logo && (
                    <div className="mb-2 md:mb-4">
                      <img
                        src={raffle.logo}
                        alt={raffle.title}
                        className="w-full h-24 md:h-32 object-contain"
                      />
                    </div>
                  )}
                  <CardTitle className="text-lg md:text-2xl">{raffle.title}</CardTitle>
                  <CardDescription>
                    <Badge variant="secondary" className="mt-1 md:mt-2 text-xs md:text-sm">
                      {getRaffleTypeLabel(raffle.raffle_type)}
                    </Badge>
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-1 md:space-y-2 p-4 md:p-6 pt-0">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 flex items-center text-sm md:text-base">
                      <DollarSign size={16} className="mr-1" />
                      Valor:
                    </span>
                    <span className="text-lg md:text-xl font-bold text-green-600">
                      ${raffle.unit_price}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 flex items-center text-sm md:text-base">
                      <Ticket size={16} className="mr-1" />
                      Disponibles:
                    </span>
                    <span className="text-lg md:text-xl font-bold text-blue-600">
                      {getAvailableCount(raffle)} / {raffle.total_numbers}
                    </span>
                  </div>
                  <div className="pt-1 md:pt-2">
                    <p className="text-xs md:text-sm font-semibold text-gray-700 mb-1">Premios:</p>
                    <ul className="text-xs md:text-sm text-gray-600 space-y-0.5 md:space-y-1">
                      {raffle.prizes.slice(0, 2).map((prize, idx) => (
                        <li key={idx}>• {prize}</li>
                      ))}
                      {raffle.prizes.length > 2 && (
                        <li className="font-semibold" style={{ color: getPrimaryColor() }}>
                          + {raffle.prizes.length - 2} premios más
                        </li>
                      )}
                    </ul>
                  </div>
                </CardContent>
                <CardFooter className="p-4 md:p-6 pt-0">
                  <Button 
                    className="w-full hover:opacity-90 text-sm md:text-base py-2 md:py-2.5" 
                    style={{ backgroundColor: getPrimaryColor() }}
                    data-testid={`view-raffle-${raffle.id}`}
                  >
                    Ver Sorteo y Elegir Números
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RafflesPage;
